import React, { useState } from 'react';
import { connect } from 'react-redux';
import { useTranslation } from 'react-i18next';
import {
  trackProfileEditError,
  trackProfileEditSave,
} from '../../models/profile/analytics';

import { ID as DialogID } from '../../components/ui/dialog';

import AdvocateService from '../../services/advocate';
import { advocateSelectors } from '../../models/advocate';
import { uiOperations } from '../../models/ui';
import { programMembershipSelectors } from '../../models/program-membership';
import { programSelectors } from '../../models/program';
import { usePushToastOrFlashMessage } from '../../components/v2/toaster/deprecation-helper';
import { useFeatureFlag } from '../../common/use-feature-flag';
import { Feature } from '../../models/features/features';

const ProfileEditContainer = ({
  children,

  analyticsData,
  syncAdvocate,

  displayOverlay,
  removeOverlay,
  advocateId,
  programMembershipStatus,
  programName,
  programId,
  programMembershipId,
  name,
  email,
  advocateFederatedId,
  programMembershipFedId,
  gdprComplianceEnabled,
  gdprComplianceContacts,
}) => {
  const [profile, setProfile] = useState({});
  const [submitting, setSubmitting] = useState(false);

  const programForgetEnabled = useFeatureFlag(Feature.PROGRAM_FORGET_ENABLED);

  const { t } = useTranslation();
  const { pushMessage } = usePushToastOrFlashMessage();

  const updateProfileField = (key, value) => {
    setProfile((p) => Object.assign({}, p, { [key]: value }));
  };

  const handleChange = (e) => {
    const { value, checked, name, type } = e.target;

    updateProfileField(name, type === 'checkbox' ? checked : value);
  };

  const handleExportClick = () => {
    generateExportDataEmail();
  };

  const handleEraseClick = () => {
    const key = 'forget-me-dialog';

    displayOverlay(
      DialogID,
      {
        title: t('screens.profile.edit_profile.forget_me'),
        children: t('screens.profile.edit_profile.forget_me_pop_up_body'),
        accept: {
          icon: 'check',
          text: t('common.continue'),
          onClick: () => {
            generateEraseDataEmail();
            removeOverlay({ key });
          },
        },
        cancel: {
          text: t('common.cancel'),
          onClick: () => {
            removeOverlay({ key });
          },
        },
      },
      key
    );
  };

  const handleSubmit = (e) => {
    e && e.preventDefault();

    setSubmitting(true);

    trackProfileEditSave(analyticsData);

    new AdvocateService(advocateId)
      .update({ ...profile })
      .then(handleSubmitSuccess)
      .catch(handleSubmitFailure);
  };

  const handleSubmitSuccess = () => {
    setSubmitting(false);

    syncAdvocate();

    pushMessage({
      text: t('screens.profile.edit_profile.profile_update_success'),
      type: 'success',
    });
  };

  const handleSubmitFailure = (e) => {
    console.error('profile edit error', e);

    const errorData = (e.response && e.response.data) || {};
    const errorMessage =
      errorData.errors && errorData.errors.length
        ? errorData.errors[0].title
        : t('errors.default');

    pushMessage({
      text: errorMessage,
      type: 'error',
    });

    trackProfileEditError(analyticsData);

    setSubmitting(false);
  };

  const gdprContactsList = gdprComplianceContacts
    ? gdprComplianceContacts.join(';')
    : '';

  /* eslint-disable */
  const generateExportDataEmail = () => {
    let mailto = gdprContactsList;

    const subject = `Request to export personal data for ${programName}`;
    const body =
      `Please export my personal data related to ${programName}. I understand the exported data will be returned to this email address.\r\n\r\n` +
      `**** Do not edit content below ****\r\n\r\n` +
      `${
        programForgetEnabled
          ? `You are receiving this data export request because you are the program’s designated privacy contact. This request only applies to the program associated with this request. No action is required for any other programs under your organization.`
          : `A user just submitted a request related to data privacy - Export All My Personal Data. As your company is the Data Controller per GDPR standards, you are receiving this request for resolution.`
      }\r\n\r\n` +
      `Please refer to the member’s identifying data below:\r\n` +
      `User: ${name}\r\n` +
      `Email: ${email || 'n/a'}\r\n` +
      `${
        programForgetEnabled
          ? `Universal Identifier: ${programMembershipFedId || 'n/a'}`
          : `Federated Identifier: ${advocateFederatedId || 'n/a'}`
      }\r\n` +
      `Program Name: ${programName}\r\n` +
      `Program ID: ${programId}\r\n\r\n` +
      `${
        !programForgetEnabled
          ? `M: ${programMembershipId}\r\nA: ${advocateId}\r\n`
          : ''
      }` +
      `For more details on how to export or delete a user’s data, please refer to this Firstup Knowledge Base article: https://support.firstup.io/hc/en-us/articles/4419515793815`;

    const encodedSubject = encodeURIComponent(subject);
    const encodedBody = encodeURIComponent(body);

    location.href = `mailto:${mailto}?subject=${encodedSubject}&body=${encodedBody}`;
  };
  /* eslint-enable */

  /* eslint-disable */
  const generateEraseDataEmail = () => {
    let mailto = gdprContactsList;

    const subject = `Request to delete personal data from ${programName}`;
    const body =
      `${
        programForgetEnabled
          ? `Please delete my personal data related to ${programName}. I understand this request will also inactivate my account and terminate community access.`
          : `Please erase my personal data related to ${programName}. I understand this request will inactivate my account in all communities tied to this account.\r\n\r\n` +
            `A user just submitted a request related to data privacy - Erase All My Personal Data. As your company is the Data Controller per GDPR standards, you are receiving this request for resolution.`
      }\r\n\r\n` +
      `**** Do not edit content below ****\r\n\r\n` +
      `You are receiving this data deletion request because you are the program’s designated privacy contact. This request only applies to the program associated with this request. No action is required for any other programs under your organization.\r\n\r\nPlease refer to the member’s identifying data below:\r\n` +
      `User: ${name}\r\n` +
      `Email: ${email || 'n/a'}\r\n` +
      `${
        programForgetEnabled
          ? `Universal Identifier: ${programMembershipFedId || 'n/a'}`
          : `Federated Identifier: ${advocateFederatedId || 'n/a'}`
      }\r\n` +
      `Program Name: ${programName}\r\n` +
      `Program ID: ${programId}\r\n\r\n` +
      `${
        !programForgetEnabled
          ? `M: ${programMembershipId}\r\nA: ${advocateId}\r\n`
          : ''
      }` +
      `For more details on how to export or delete a user’s data, please refer to this Firstup Knowledge Base article: https://support.firstup.io/hc/en-us/articles/4419515793815`;

    const encodedSubject = encodeURIComponent(subject);
    const encodedBody = encodeURIComponent(body);

    location.href = `mailto:${mailto}?subject=${encodedSubject}&body=${encodedBody}`;
  };
  /* eslint-enable */

  const allowedStatuses = ['active', 'registering'];
  const advocateStatus = programMembershipStatus;
  const displayGdprOptions =
    gdprComplianceEnabled &&
    gdprComplianceContacts &&
    allowedStatuses.includes(advocateStatus);

  return children({
    updatedValues: profile,
    submitting,
    displayGdprOptions,
    onChange: handleChange,
    onExportClick: handleExportClick,
    onEraseClick: handleEraseClick,
    onSubmit: handleSubmit,
    generateEraseDataEmail: generateEraseDataEmail,
  });
};

const mapStateToProps = (state, ownProps) => {
  return {
    gdprComplianceEnabled: programSelectors.getProgramGdprComplianceEnabled(
      state,
      ownProps
    ),
    gdprComplianceContacts: programSelectors.getProgramGdprComplianceContacts(
      state,
      ownProps
    ),
    programName: programSelectors.getProgramName(state, ownProps),
    programId: programSelectors.getProgramId(state, ownProps),
    programMembershipId: programMembershipSelectors.getProgramMembershipId(
      state,
      ownProps
    ),
    programMembershipStatus:
      programMembershipSelectors.getProgramMembershipStatus(state, ownProps),
    programMembershipFedId:
      programMembershipSelectors.getProgramMembershipFedId(state, ownProps),
    advocateId: advocateSelectors.getAdvocateId(state, ownProps),
    name: advocateSelectors.getAdvocateName(state, ownProps),
    email: advocateSelectors.getAdvocateEmail(state, ownProps),
    advocateFederatedId: advocateSelectors.getAdvocateFederatedId(
      state,
      ownProps
    ),
  };
};

const mapDispatchToProps = {
  displayOverlay: uiOperations.displayOverlay,
  removeOverlay: uiOperations.removeOverlay,
};

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(ProfileEditContainer);
